import { ApiClient } from '../../client';
import { ApiClientOptions } from '../../types';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  ProductsQueryParams,
  ProductResponse,
  ProductMasterItem,
  ProductCountResponse,
  ProductUploadRequest,
  ProductUploadResponse,
  CommitChangesRequest,
  ProductQueryResponse,
  HierarchicalProductQueryResponse,
  FlatProductQueryResponse,
} from './types';
import { convertDatesToStrings } from '../../../../utils/dateConversion';

export class ProductService {
  constructor(private api: ApiClient) {}

  async getAll(params?: ProductsQueryParams) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = `/products${query ? `?${query}` : ''}`;
    return this.api.get<ProductQueryResponse>(url);
  }

  async getCount(search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get<ProductCountResponse>(`/products/count${query}`);
  }

  async getById(id: number) {
    return this.api.get<Product>(`/products/${id}`);
  }

  async create(data: CreateProductRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.post<ProductResponse>('/products', payload);
  }

  async update(data: UpdateProductRequest) {
    const payload = convertDatesToStrings(data);
    return this.api.put<ProductResponse>(
      `/products/${data.product_id}`,
      payload
    );
  }

  async delete(id: number) {
    return this.api.delete(`/products/${id}`);
  }

  async deleteAll() {
    return this.api.delete('/products');
  }

  // Product Master endpoints
  async getMasterProducts() {
    return this.api.get<ProductMasterItem[]>('/product-master');
  }

  async updateMasterProduct(id: number, data: UpdateProductRequest) {
    return this.api.put<ProductMasterItem>(`/product-master/${id}`, data);
  }

  // File upload endpoints
  async uploadProducts(data: ProductUploadRequest) {
    const formData = new FormData();
    formData.append('file', data.file);
    if (Array.isArray(data.manufacturer_ids)) {
      data.manufacturer_ids.forEach(id => {
        formData.append('manufacturer_id', id.toString());
      });
    }

    return this.api.post<ProductUploadResponse>('/uploadProducts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async commitChanges(data: CommitChangesRequest) {
    return this.api.post('/commitProducts', {
      manufacturer_ids: Array.isArray(data.manufacturer_ids)
        ? data.manufacturer_ids
        : [],
      differences: JSON.stringify(data.differences),
    });
  }

  // Hierarchical products endpoint (AB test: multiple_column_grouping)
  async getAllHierarchical(params?: ProductsQueryParams) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = `/products/hierarchical${query ? `?${query}` : ''}`;
    return this.api.get<HierarchicalProductQueryResponse>(url);
  }

  async getCountHierarchical(search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get<ProductCountResponse>(
      `/products/hierarchical/count${query}`
    );
  }

  // Flat products endpoint (AB test: multiple_column_grouping - row grouping variant)
  // Returns denormalized Cartesian product for MUI DataGrid row grouping
  async getAllFlat(
    params?: ProductsQueryParams,
    requestConfig?: ApiClientOptions
  ) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.advancedSearch)
      queryParams.append(
        'advancedSearch',
        JSON.stringify(params.advancedSearch)
      );
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection)
      queryParams.append('sortDirection', params.sortDirection);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = `/products/flat${query ? `?${query}` : ''}`;
    return this.api.get<FlatProductQueryResponse>(url, requestConfig);
  }

  async getCountFlat(search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.api.get<ProductCountResponse>(`/products/flat/count${query}`);
  }
}
